/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateEndCustomer } from '../models/create-end-customer';
import { CreateYtjDataFetchRequest } from '../models/create-ytj-data-fetch-request';
import { EndCustomer } from '../models/end-customer';
import { EndCustomerContact } from '../models/end-customer-contact';
import { EndCustomerDuplicateGroup } from '../models/end-customer-duplicate-group';
import { EndCustomerKeepSeparateRequest } from '../models/end-customer-keep-separate-request';
import { EndCustomerMergePreview } from '../models/end-customer-merge-preview';
import { EndCustomerMergeRequest } from '../models/end-customer-merge-request';
import { EndCustomersResponsePage } from '../models/end-customers-response-page';
import { InvoicesResponsePage } from '../models/invoices-response-page';
import { YtjDataSnapshot } from '../models/ytj-data-snapshot';


/**
 * End Customers API
 */
@Injectable({
  providedIn: 'root',
})
export class EndCustomersService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listEndCustomers
   */
  static readonly ListEndCustomersPath = '/end-customers';

  /**
   * List end customers.
   *
   * Searches our end customers, as the extraction review form's end-customer
   * search does: those whose name or business ID holds `q`, ignoring case,
   * or every one when `q` is blank. They come in name order, an end customer
   * without a name first, a page at a time, each with its contacts. End
   * customers stored twice are listed as stored. Needs only an
   * authenticated caller, as `listClients` does.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listEndCustomers()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomers$Response(params?: {

    /**
     * Part of the end customer&#x27;s name or business ID, matched ignoring case and the spaces around it.
     */
    'q'?: string;

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EndCustomersResponsePage>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.ListEndCustomersPath, 'get');
    if (params) {
      rb.query('q', params['q'], {});
      rb.query('limit', params.limit, {});
      rb.query('after', params.after, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomersResponsePage>;
      })
    );
  }

  /**
   * List end customers.
   *
   * Searches our end customers, as the extraction review form's end-customer
   * search does: those whose name or business ID holds `q`, ignoring case,
   * or every one when `q` is blank. They come in name order, an end customer
   * without a name first, a page at a time, each with its contacts. End
   * customers stored twice are listed as stored. Needs only an
   * authenticated caller, as `listClients` does.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listEndCustomers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomers(params?: {

    /**
     * Part of the end customer&#x27;s name or business ID, matched ignoring case and the spaces around it.
     */
    'q'?: string;

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;
    context?: HttpContext
  }
): Observable<EndCustomersResponsePage> {

    return this.listEndCustomers$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomersResponsePage>) => r.body as EndCustomersResponsePage)
    );
  }

  /**
   * Path part for operation createEndCustomer
   */
  static readonly CreateEndCustomerPath = '/end-customers';

  /**
   * Create end customer.
   *
   * Create a new end customer. Requires the `end-customers:create` permission.
   * An end customer is identified by its `business_id`: a request without one is refused with a 422 saying the business ID is required.
   * A `country` that is not an ISO 3166-1 alpha-2 code in upper case, such as `FI` or `ES`, is refused with a 422 whose `errors` name `country`. An empty or absent `country` means Finland.
   * If a valid Finnish `business_id` is provided, the API will attempt a synchronous lookup from the YTJ service to enrich the data.
   * If the lookup is successful, the `name`, `company_form`, and `company_situation` will be populated from YTJ. The `name` from YTJ is only used if the `name` field in the request body is empty.
   * If the YTJ lookup fails or times out, the end customer will be created using only the data provided in the request.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createEndCustomer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEndCustomer$Response(params: {
    context?: HttpContext
    body: CreateEndCustomer
  }
): Observable<StrictHttpResponse<EndCustomer>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.CreateEndCustomerPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomer>;
      })
    );
  }

  /**
   * Create end customer.
   *
   * Create a new end customer. Requires the `end-customers:create` permission.
   * An end customer is identified by its `business_id`: a request without one is refused with a 422 saying the business ID is required.
   * A `country` that is not an ISO 3166-1 alpha-2 code in upper case, such as `FI` or `ES`, is refused with a 422 whose `errors` name `country`. An empty or absent `country` means Finland.
   * If a valid Finnish `business_id` is provided, the API will attempt a synchronous lookup from the YTJ service to enrich the data.
   * If the lookup is successful, the `name`, `company_form`, and `company_situation` will be populated from YTJ. The `name` from YTJ is only used if the `name` field in the request body is empty.
   * If the YTJ lookup fails or times out, the end customer will be created using only the data provided in the request.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createEndCustomer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEndCustomer(params: {
    context?: HttpContext
    body: CreateEndCustomer
  }
): Observable<EndCustomer> {

    return this.createEndCustomer$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomer>) => r.body as EndCustomer)
    );
  }

  /**
   * Path part for operation listEndCustomerDuplicates
   */
  static readonly ListEndCustomerDuplicatesPath = '/end-customers/duplicates';

  /**
   * List end-customer duplicate groups.
   *
   * List end-customer duplicate groups: for every non-empty trimmed
   * business id held by two or more end customers, one group with the
   * trimmed id and every member's id, raw business id, name, city, and
   * contact/invoice counts. Groups resolved via keep-separate are hidden
   * while their member set is unchanged and resurface (flagged) when a new
   * twin appears. Requires the `end-customers:merge` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listEndCustomerDuplicates()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomerDuplicates$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<EndCustomerDuplicateGroup>>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.ListEndCustomerDuplicatesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<EndCustomerDuplicateGroup>>;
      })
    );
  }

  /**
   * List end-customer duplicate groups.
   *
   * List end-customer duplicate groups: for every non-empty trimmed
   * business id held by two or more end customers, one group with the
   * trimmed id and every member's id, raw business id, name, city, and
   * contact/invoice counts. Groups resolved via keep-separate are hidden
   * while their member set is unchanged and resurface (flagged) when a new
   * twin appears. Requires the `end-customers:merge` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listEndCustomerDuplicates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomerDuplicates(params?: {
    context?: HttpContext
  }
): Observable<Array<EndCustomerDuplicateGroup>> {

    return this.listEndCustomerDuplicates$Response(params).pipe(
      map((r: StrictHttpResponse<Array<EndCustomerDuplicateGroup>>) => r.body as Array<EndCustomerDuplicateGroup>)
    );
  }

  /**
   * Path part for operation getEndCustomerMergePreview
   */
  static readonly GetEndCustomerMergePreviewPath = '/end-customers/duplicates/{business_id}/merge-preview';

  /**
   * Preview an end-customer merge.
   *
   * For a (source, destination) pair in the same duplicate group, return
   * both rows' pick-group values (flagging groups that differ or where only
   * one side has a value), related-data facts, discount overlaps, the
   * remaining group members, and the concurrency version tokens. Requires
   * the `end-customers:merge` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEndCustomerMergePreview()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerMergePreview$Response(params: {

    /**
     * The trimmed business id identifying the duplicate group.
     */
    business_id: string;

    /**
     * The higher-id member being merged away.
     */
    source_id: number;

    /**
     * The surviving (lowest-id) member.
     */
    destination_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EndCustomerMergePreview>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.GetEndCustomerMergePreviewPath, 'get');
    if (params) {
      rb.path('business_id', params.business_id, {});
      rb.query('source_id', params.source_id, {});
      rb.query('destination_id', params.destination_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomerMergePreview>;
      })
    );
  }

  /**
   * Preview an end-customer merge.
   *
   * For a (source, destination) pair in the same duplicate group, return
   * both rows' pick-group values (flagging groups that differ or where only
   * one side has a value), related-data facts, discount overlaps, the
   * remaining group members, and the concurrency version tokens. Requires
   * the `end-customers:merge` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEndCustomerMergePreview$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerMergePreview(params: {

    /**
     * The trimmed business id identifying the duplicate group.
     */
    business_id: string;

    /**
     * The higher-id member being merged away.
     */
    source_id: number;

    /**
     * The surviving (lowest-id) member.
     */
    destination_id: number;
    context?: HttpContext
  }
): Observable<EndCustomerMergePreview> {

    return this.getEndCustomerMergePreview$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomerMergePreview>) => r.body as EndCustomerMergePreview)
    );
  }

  /**
   * Path part for operation mergeEndCustomers
   */
  static readonly MergeEndCustomersPath = '/end-customers/merge';

  /**
   * Merge two end customers.
   *
   * Collapse a whitespace-duplicate pair: re-parent the source row's
   * related data onto the destination (lowest id), delete the source, and
   * write the destination's picked field values in one transaction. The
   * client sends picks (which row each field group comes from), never field
   * values. Requires the `end-customers:merge` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `mergeEndCustomers()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  mergeEndCustomers$Response(params: {
    context?: HttpContext
    body: EndCustomerMergeRequest
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.MergeEndCustomersPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Merge two end customers.
   *
   * Collapse a whitespace-duplicate pair: re-parent the source row's
   * related data onto the destination (lowest id), delete the source, and
   * write the destination's picked field values in one transaction. The
   * client sends picks (which row each field group comes from), never field
   * values. Requires the `end-customers:merge` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `mergeEndCustomers$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  mergeEndCustomers(params: {
    context?: HttpContext
    body: EndCustomerMergeRequest
  }
): Observable<void> {

    return this.mergeEndCustomers$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation keepEndCustomersSeparate
   */
  static readonly KeepEndCustomersSeparatePath = '/end-customers/duplicates/{business_id}/keep-separate';

  /**
   * Mark a duplicate group as distinct customers.
   *
   * Resolve a duplicate group as legitimately distinct: trim every padded
   * member's business id in place and record a persistent dismissal so the
   * group leaves the duplicates list. Requires the `end-customers:merge`
   * permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `keepEndCustomersSeparate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  keepEndCustomersSeparate$Response(params: {

    /**
     * The trimmed business id identifying the duplicate group.
     */
    business_id: string;
    context?: HttpContext
    body?: EndCustomerKeepSeparateRequest
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.KeepEndCustomersSeparatePath, 'post');
    if (params) {
      rb.path('business_id', params.business_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Mark a duplicate group as distinct customers.
   *
   * Resolve a duplicate group as legitimately distinct: trim every padded
   * member's business id in place and record a persistent dismissal so the
   * group leaves the duplicates list. Requires the `end-customers:merge`
   * permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `keepEndCustomersSeparate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  keepEndCustomersSeparate(params: {

    /**
     * The trimmed business id identifying the duplicate group.
     */
    business_id: string;
    context?: HttpContext
    body?: EndCustomerKeepSeparateRequest
  }
): Observable<void> {

    return this.keepEndCustomersSeparate$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getEndCustomer
   */
  static readonly GetEndCustomerPath = '/end-customers/{id}';

  /**
   * Get end customer.
   *
   * Get end customer
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEndCustomer()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomer$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EndCustomer>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.GetEndCustomerPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomer>;
      })
    );
  }

  /**
   * Get end customer.
   *
   * Get end customer
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEndCustomer$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomer(params: {
    id: number;
    context?: HttpContext
  }
): Observable<EndCustomer> {

    return this.getEndCustomer$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomer>) => r.body as EndCustomer)
    );
  }

  /**
   * Path part for operation updateEndCustomer
   */
  static readonly UpdateEndCustomerPath = '/end-customers/{id}';

  /**
   * Update end customer.
   *
   * Update end customer. Requires the `end-customers:edit` permission.
   * Two changes are refused with a 422 whose `errors` name the field, and nothing is saved:
   * - a `business_id` that differs from the stored one, since an end customer's business ID cannot be changed after creation;
   * - a `country` that differs from the stored one and is not an ISO 3166-1 alpha-2 code in upper case, such as `FI` or `ES`. An empty `country` is not refused, and neither is a stored one sent back unchanged, so an end customer stored before this rule still saves.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateEndCustomer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEndCustomer$Response(params: {
    id: number;
    context?: HttpContext
    body: EndCustomer
  }
): Observable<StrictHttpResponse<EndCustomer>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.UpdateEndCustomerPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomer>;
      })
    );
  }

  /**
   * Update end customer.
   *
   * Update end customer. Requires the `end-customers:edit` permission.
   * Two changes are refused with a 422 whose `errors` name the field, and nothing is saved:
   * - a `business_id` that differs from the stored one, since an end customer's business ID cannot be changed after creation;
   * - a `country` that differs from the stored one and is not an ISO 3166-1 alpha-2 code in upper case, such as `FI` or `ES`. An empty `country` is not refused, and neither is a stored one sent back unchanged, so an end customer stored before this rule still saves.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateEndCustomer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEndCustomer(params: {
    id: number;
    context?: HttpContext
    body: EndCustomer
  }
): Observable<EndCustomer> {

    return this.updateEndCustomer$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomer>) => r.body as EndCustomer)
    );
  }

  /**
   * Path part for operation deleteEndCustomer
   */
  static readonly DeleteEndCustomerPath = '/end-customers/{id}';

  /**
   * Delete end customer.
   *
   * Delete end customer
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteEndCustomer()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteEndCustomer$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.DeleteEndCustomerPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete end customer.
   *
   * Delete end customer
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteEndCustomer$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteEndCustomer(params: {
    id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteEndCustomer$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createYtjDataFetchForEndCustomer
   */
  static readonly CreateYtjDataFetchForEndCustomerPath = '/end-customers/{id}/ytj-data-fetches';

  /**
   * Create a YTJ data fetch for an end customer.
   *
   * Asynchronously fetches the latest company data from the YTJ service for the given end customer.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createYtjDataFetchForEndCustomer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createYtjDataFetchForEndCustomer$Response(params: {
    id: number;
    context?: HttpContext

    /**
     * Optional body to force a fetch even if recent data exists.
     */
    body?: CreateYtjDataFetchRequest
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.CreateYtjDataFetchForEndCustomerPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Create a YTJ data fetch for an end customer.
   *
   * Asynchronously fetches the latest company data from the YTJ service for the given end customer.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createYtjDataFetchForEndCustomer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createYtjDataFetchForEndCustomer(params: {
    id: number;
    context?: HttpContext

    /**
     * Optional body to force a fetch even if recent data exists.
     */
    body?: CreateYtjDataFetchRequest
  }
): Observable<void> {

    return this.createYtjDataFetchForEndCustomer$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listEndCustomerYtjDataSnapshots
   */
  static readonly ListEndCustomerYtjDataSnapshotsPath = '/end-customers/{id}/ytj-data-snapshots';

  /**
   * Get end customer's YTJ data snapshots.
   *
   * Returns a list of all historical YTJ data snapshots for the given end customer.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listEndCustomerYtjDataSnapshots()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomerYtjDataSnapshots$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<YtjDataSnapshot>>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.ListEndCustomerYtjDataSnapshotsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<YtjDataSnapshot>>;
      })
    );
  }

  /**
   * Get end customer's YTJ data snapshots.
   *
   * Returns a list of all historical YTJ data snapshots for the given end customer.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listEndCustomerYtjDataSnapshots$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEndCustomerYtjDataSnapshots(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<YtjDataSnapshot>> {

    return this.listEndCustomerYtjDataSnapshots$Response(params).pipe(
      map((r: StrictHttpResponse<Array<YtjDataSnapshot>>) => r.body as Array<YtjDataSnapshot>)
    );
  }

  /**
   * Path part for operation getEndCustomerContacts
   */
  static readonly GetEndCustomerContactsPath = '/end-customers/{end_customer_id}/contacts';

  /**
   * Get contacts for an end customer.
   *
   * Get contacts for an end customer
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEndCustomerContacts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerContacts$Response(params: {
    end_customer_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<EndCustomerContact>>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.GetEndCustomerContactsPath, 'get');
    if (params) {
      rb.path('end_customer_id', params.end_customer_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<EndCustomerContact>>;
      })
    );
  }

  /**
   * Get contacts for an end customer.
   *
   * Get contacts for an end customer
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEndCustomerContacts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerContacts(params: {
    end_customer_id: number;
    context?: HttpContext
  }
): Observable<Array<EndCustomerContact>> {

    return this.getEndCustomerContacts$Response(params).pipe(
      map((r: StrictHttpResponse<Array<EndCustomerContact>>) => r.body as Array<EndCustomerContact>)
    );
  }

  /**
   * Path part for operation createEndCustomerContact
   */
  static readonly CreateEndCustomerContactPath = '/end-customers/{end_customer_id}/contacts';

  /**
   * Create a contact for an end customer.
   *
   * Create a contact for an end customer
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createEndCustomerContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEndCustomerContact$Response(params: {
    end_customer_id: number;
    context?: HttpContext
    body: EndCustomerContact
  }
): Observable<StrictHttpResponse<EndCustomerContact>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.CreateEndCustomerContactPath, 'post');
    if (params) {
      rb.path('end_customer_id', params.end_customer_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomerContact>;
      })
    );
  }

  /**
   * Create a contact for an end customer.
   *
   * Create a contact for an end customer
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createEndCustomerContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEndCustomerContact(params: {
    end_customer_id: number;
    context?: HttpContext
    body: EndCustomerContact
  }
): Observable<EndCustomerContact> {

    return this.createEndCustomerContact$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomerContact>) => r.body as EndCustomerContact)
    );
  }

  /**
   * Path part for operation getEndCustomerContact
   */
  static readonly GetEndCustomerContactPath = '/end-customers/{end_customer_id}/contacts/{id}';

  /**
   * Get one contact.
   *
   * Get one contact
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEndCustomerContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerContact$Response(params: {
    end_customer_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EndCustomerContact>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.GetEndCustomerContactPath, 'get');
    if (params) {
      rb.path('end_customer_id', params.end_customer_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomerContact>;
      })
    );
  }

  /**
   * Get one contact.
   *
   * Get one contact
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEndCustomerContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerContact(params: {
    end_customer_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<EndCustomerContact> {

    return this.getEndCustomerContact$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomerContact>) => r.body as EndCustomerContact)
    );
  }

  /**
   * Path part for operation updateEndCustomerContact
   */
  static readonly UpdateEndCustomerContactPath = '/end-customers/{end_customer_id}/contacts/{id}';

  /**
   * Update one contact.
   *
   * Update one contact
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateEndCustomerContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEndCustomerContact$Response(params: {
    end_customer_id: number;
    id: number;
    context?: HttpContext
    body: EndCustomerContact
  }
): Observable<StrictHttpResponse<EndCustomerContact>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.UpdateEndCustomerContactPath, 'put');
    if (params) {
      rb.path('end_customer_id', params.end_customer_id, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EndCustomerContact>;
      })
    );
  }

  /**
   * Update one contact.
   *
   * Update one contact
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateEndCustomerContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEndCustomerContact(params: {
    end_customer_id: number;
    id: number;
    context?: HttpContext
    body: EndCustomerContact
  }
): Observable<EndCustomerContact> {

    return this.updateEndCustomerContact$Response(params).pipe(
      map((r: StrictHttpResponse<EndCustomerContact>) => r.body as EndCustomerContact)
    );
  }

  /**
   * Path part for operation deleteEndCustomerContact
   */
  static readonly DeleteEndCustomerContactPath = '/end-customers/{end_customer_id}/contacts/{id}';

  /**
   * Delete the contact.
   *
   * Delete the contact
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteEndCustomerContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteEndCustomerContact$Response(params: {
    end_customer_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.DeleteEndCustomerContactPath, 'delete');
    if (params) {
      rb.path('end_customer_id', params.end_customer_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete the contact.
   *
   * Delete the contact
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteEndCustomerContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteEndCustomerContact(params: {
    end_customer_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteEndCustomerContact$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getEndCustomerInvoices
   */
  static readonly GetEndCustomerInvoicesPath = '/end-customers/{end_customer_id}/invoices';

  /**
   * Get invoices for an end customer.
   *
   * Get invoices for an end customer:w
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEndCustomerInvoices()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerInvoices$Response(params: {
    end_customer_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<InvoicesResponsePage>> {

    const rb = new RequestBuilder(this.rootUrl, EndCustomersService.GetEndCustomerInvoicesPath, 'get');
    if (params) {
      rb.path('end_customer_id', params.end_customer_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<InvoicesResponsePage>;
      })
    );
  }

  /**
   * Get invoices for an end customer.
   *
   * Get invoices for an end customer:w
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEndCustomerInvoices$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerInvoices(params: {
    end_customer_id: number;
    context?: HttpContext
  }
): Observable<InvoicesResponsePage> {

    return this.getEndCustomerInvoices$Response(params).pipe(
      map((r: StrictHttpResponse<InvoicesResponsePage>) => r.body as InvoicesResponsePage)
    );
  }

}
